import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { ArrowLeft, RotateCcw, Trophy, Coins, Heart } from "lucide-react";
import ArcadeNav from "../../arcade/ArcadeNav";
import { useAuth } from "../../contexts/AuthContext";
import { usePresence } from "../../game/presence";
import useSEO from "../../seo/useSEO";
import { SEO_GAMES } from "../../seo/seoData";
import GameSEOContent from "../../seo/GameSEOContent";
import ArcadeFooter from "../../arcade/ArcadeFooter";
import {
  newGame, trySwap, hasAnyMove, shuffleBoard,
  ROWS, COLS, POWER_NAME,
} from "./CrushEngine";
import { tileImg, tileMeta } from "./tiles";
import "./crush.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BEST_KEY = "zombie-crush-best";

export default function CrushScreen() {
  useSEO(SEO_GAMES["zombie-crush"]);
  const { user, refresh } = useAuth();

  const [state, setState] = useState(() => newGame(1));
  const [selected, setSelected] = useState(null); // { r, c }
  const [shake, setShake] = useState(null);       // { r, c }
  const [busy, setBusy] = useState(false);
  const [won, setWon] = useState(false);
  const [lost, setLost] = useState(false);
  const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem(BEST_KEY) || "0", 10));
  const [resultSubmitted, setResultSubmitted] = useState(false);
  const drag = useRef(null); // { r, c, x, y }

  usePresence(true, "zombie-crush");

  // Persist personal best
  useEffect(() => {
    if (state.score > bestScore) {
      setBestScore(state.score);
      localStorage.setItem(BEST_KEY, String(state.score));
    }
  }, [state.score, bestScore]);

  // Win / lose detection — runs whenever state changes.
  useEffect(() => {
    if (won || lost) return;
    if (state.score >= state.goal) {
      setWon(true);
      submitResult(true);
      return;
    }
    if (state.moves <= 0) {
      setLost(true);
      submitResult(false);
    }
  }, [state.score, state.moves, state.goal, won, lost]);

  // No-moves detection (auto-shuffle).
  useEffect(() => {
    if (busy || won || lost) return;
    if (!hasAnyMove(state.grid, state.powers)) {
      toast("No moves left — shuffling!", { duration: 1600 });
      setState((s) => shuffleBoard(s));
    }
  }, [state.grid, state.powers, busy, won, lost]);

  async function submitResult(victory) {
    if (resultSubmitted) return;
    setResultSubmitted(true);
    if (!user) return;
    try {
      const res = await axios.post(`${API}/games/crush/win`, {
        score: state.score,
        level: state.level,
        moves_used: 25 - state.moves,
        victory,
      });
      if (res.data?.coins_awarded > 0) {
        toast.success(`+${res.data.coins_awarded} coins!`, { duration: 4000 });
        await refresh();
      }
    } catch (_e) { /* silent */ }
  }

  function handleNew() {
    setState(newGame(state.level));
    setSelected(null);
    setShake(null);
    setBusy(false);
    setWon(false);
    setLost(false);
    setResultSubmitted(false);
  }

  function handleNextLevel() {
    setState(newGame(state.level + 1));
    setSelected(null);
    setBusy(false);
    setWon(false);
    setLost(false);
    setResultSubmitted(false);
  }

  // ---- Swap dispatcher -----------------------------------------------------
  const doSwap = useCallback((r1, c1, r2, c2) => {
    if (busy || won || lost) return;
    const res = trySwap(state, r1, c1, r2, c2);
    if (!res.ok) {
      setShake({ r: r1, c: c1 });
      setTimeout(() => setShake(null), 400);
      return;
    }
    setBusy(true);
    setState(res.state);
    setSelected(null);
    // Surface power-tile events to the player so the new mechanic is
    // discoverable. We aggregate across the cascade and only toast on the
    // first step's events (the subsequent cascades are unrelated chain).
    if (res.steps && res.steps.length) {
      const announced = new Set();
      for (const step of res.steps) {
        for (const spawn of step.spawned || []) {
          const label = POWER_NAME[spawn.power];
          if (label && !announced.has(label)) {
            announced.add(label);
            toast.success(label, { duration: 1600 });
          }
        }
        for (const act of step.activated || []) {
          // Only call out the headline "Apocalypse" when a color-bomb fires.
          if (act.power === "color-bomb" && !announced.has("Zombie Apocalypse!")) {
            announced.add("Zombie Apocalypse!");
            toast("Zombie Apocalypse!", { duration: 1800 });
          }
        }
      }
    }
    setTimeout(() => setBusy(false), 350);
  }, [busy, won, lost, state]);

  // ---- Tap-to-select OR drag-swap -----------------------------------------
  function onTilePointerDown(e, r, c) {
    if (busy || won || lost) return;
    drag.current = { r, c, x: e.clientX, y: e.clientY, moved: false };
    // Capture pointer for proper move/up routing on touch.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }

  function onTilePointerMove(e) {
    if (!drag.current || drag.current.moved) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    const THRESH = 20;
    if (Math.max(ax, ay) < THRESH) return;
    drag.current.moved = true;
    let dr = 0, dc = 0;
    if (ax > ay) dc = dx > 0 ? 1 : -1;
    else dr = dy > 0 ? 1 : -1;
    const r2 = drag.current.r + dr;
    const c2 = drag.current.c + dc;
    if (r2 < 0 || r2 >= ROWS || c2 < 0 || c2 >= COLS) return;
    doSwap(drag.current.r, drag.current.c, r2, c2);
    drag.current = null;
  }

  function onTilePointerUp(e, r, c) {
    if (!drag.current) return;
    if (!drag.current.moved) {
      // Tap — handle as tap-to-select / second-tap-to-swap.
      const sel = selected;
      if (!sel) {
        setSelected({ r, c });
      } else if (sel.r === r && sel.c === c) {
        setSelected(null);
      } else if (Math.abs(sel.r - r) + Math.abs(sel.c - c) === 1) {
        doSwap(sel.r, sel.c, r, c);
      } else {
        setSelected({ r, c });
      }
    }
    drag.current = null;
  }

  // ---- Render --------------------------------------------------------------
  const cellPctW = 100 / COLS;
  const cellPctH = 100 / ROWS;

  return (
    <div className="arcade-page crush-page" data-testid="crush-board">
      <Toaster position="top-center" theme="dark" />
      <ArcadeNav />

      <div className="arcade-bg" aria-hidden="true">
        <div className="arcade-bg-glow arcade-bg-glow-1" />
        <div className="arcade-bg-glow arcade-bg-glow-2" />
        <div className="arcade-bg-grid" />
      </div>

      <main className="arcade-main">
        <div className="zc3-header">
          <Link to="/" className="zc-btn ghost zc3-back" data-testid="crush-back">
            <ArrowLeft size={14} /> Arcade
          </Link>
          <div className="zc3-stats">
            <div className="zc3-stat zc3-stat-level" data-testid="crush-level">
              <span className="zc3-stat-label">Lvl</span>
              <b>{state.level}</b>
            </div>
            <div className="zc3-stat zc3-stat-score" data-testid="crush-score">
              <span className="zc3-stat-label">Score</span>
              <b>{state.score.toLocaleString()}</b>
              <em>/ {state.goal.toLocaleString()}</em>
            </div>
            <div className="zc3-stat zc3-stat-moves" data-testid="crush-moves">
              <span className="zc3-stat-label"><Heart size={11}/> Moves</span>
              <b>{state.moves}</b>
            </div>
          </div>
          <button type="button" className="zc-btn ghost" onClick={handleNew} data-testid="crush-new">
            <RotateCcw size={14} /> New
          </button>
        </div>

        {/* Goal progress bar */}
        <div className="zc3-progress" data-testid="crush-progress">
          <div
            className="zc3-progress-fill"
            style={{ width: `${Math.min(100, (state.score / state.goal) * 100)}%` }}
          />
        </div>

        <div className="zc3-board-wrap">
          <div
            className="zc3-board"
            data-testid="crush-grid"
            style={{ aspectRatio: `${COLS} / ${ROWS}` }}
          >
            {state.grid.map((row, r) =>
              row.map((type, c) => {
                if (!type) return null;
                const id = state.tileIds[r][c];
                const meta = tileMeta(type);
                const img = tileImg(type);
                const power = state.powers && state.powers[r] ? state.powers[r][c] : null;
                const isSelected = selected && selected.r === r && selected.c === c;
                const isShaking = shake && shake.r === r && shake.c === c;
                return (
                  <div
                    key={id}
                    className={`zc3-tile ${isSelected ? "is-selected" : ""} ${isShaking ? "is-shake" : ""} ${power ? `is-power power-${power}` : ""}`}
                    style={{
                      left: `${c * cellPctW}%`,
                      top: `${r * cellPctH}%`,
                      width: `${cellPctW}%`,
                      height: `${cellPctH}%`,
                      ["--tile-accent"]: meta.accent,
                    }}
                    onPointerDown={(e) => onTilePointerDown(e, r, c)}
                    onPointerMove={onTilePointerMove}
                    onPointerUp={(e) => onTilePointerUp(e, r, c)}
                    onPointerCancel={() => { drag.current = null; }}
                    data-testid={`crush-tile-${r}-${c}`}
                    data-power={power || ""}
                  >
                    <div className="zc3-tile-inner">
                      {img ? (
                        <img src={img} alt={meta.name} className="zc3-tile-img" draggable={false} />
                      ) : (
                        <span className="zc3-tile-fallback">{meta.name[0]}</span>
                      )}
                      {power && <span className="zc3-power-fx" aria-hidden="true" />}
                    </div>
                  </div>
                );
              })
            )}

            {(won || lost) && (
              <div className="zc3-overlay" data-testid={won ? "crush-win" : "crush-lose"}>
                <div className="zc3-overlay-card">
                  {won ? (
                    <>
                      <Trophy size={32} />
                      <h2>Level {state.level} Cleared!</h2>
                      <p>Score: <b>{state.score.toLocaleString()}</b></p>
                      <p>Moves left: <b>{state.moves}</b></p>
                      <div className="zc3-overlay-buttons">
                        <button type="button" className="zc-btn primary" onClick={handleNextLevel} data-testid="crush-next-level">
                          Next level →
                        </button>
                        <button type="button" className="zc-btn ghost" onClick={handleNew} data-testid="crush-replay">
                          <RotateCcw size={14} /> Replay
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2>Out of moves</h2>
                      <p>Score: <b>{state.score.toLocaleString()}</b> / {state.goal.toLocaleString()}</p>
                      <button type="button" className="zc-btn primary" onClick={handleNew} data-testid="crush-retry">
                        <RotateCcw size={14} /> Try again
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {!user && (
          <div className="zc3-anon-note">
            <Coins size={12} /> Sign in to earn coins for clearing levels.
          </div>
        )}

        <GameSEOContent gameId="zombie-crush" />
        <ArcadeFooter />
      </main>
    </div>
  );
}
